import React, { useState, useEffect } from 'react';
import './CreatePurchaseOrderPage.css';
import { useNavigate } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import Header from "../../components/Header";
import LoadingSpinner from '../../components/placeholders/LoadingSpinner';
import httpClient from '../../components/helpers/httpClient';
import { toast } from "react-toastify";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Label from '../../components/Label';
import { unitStatusOptions, unitStatusLabelColors } from '../../assets/data/data'


function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchUnits(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchUnits = async (search = '') => {
    try {
      if (search) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }

      const response = await httpClient.post(import.meta.env.VITE_API_URL + "/api/units/get", {
        filters: null,
        search: search.trim() || null
      });

      if (response.status === 200) {
        setUnits(response.data);
      } else {
        toast.error("Failed to fetch units");
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      toast.error("Failed to fetch units");
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const handleCreatePurchaseOrder = async (unit) => {
    if (!unit?.id) {
      toast.error("Invalid unit selected");
      return;
    }

    try {
      setIsCreating(true);
      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + "/api/purchase-orders/create",
        {
          unit_id: unit.id
        }
      );

      if (response.status === 200 || response.status === 201) {
        const newPoId = response.data.purchaseOrderId || response.data.id;

        toast.success(`Purchase order created successfully for ${unit.unit_name || `Unit ${unit.id}`}!`);
        navigate(`/purchase-orders/${newPoId}`);
      } else {
        toast.error(response.data?.message || "Failed to create purchase order");
      }
    } catch (error) {
      console.error('Error creating purchase order:', error);
      toast.error(error.response?.data?.message || "Failed to create purchase order");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  if (isLoading) {
    return (
      <div className='template'>
        <Header>
          <div className="back_button" onClick={() => navigate(-1)}>
            <KeyboardBackspaceIcon /> Go Back
          </div>
        </Header>
        <MenuBar />
        <div className="template__body" id="body-create-purchase-order">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className='template'>
      <Header>
        <div className="header__left--create-po">
          <div className="back_button" onClick={() => navigate(-1)}>
            <KeyboardBackspaceIcon /> Go Back
          </div>
        </div>
        <div className="header__filters">
          <div className="search-bar" onClick={handleClearSearch}>
            <input
              placeholder="Search units, buildings, or projects..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm === "" ? <SearchIcon /> : <ClearIcon />}
          </div>
        </div>
      </Header>
      <MenuBar />
      <div className="template__body" id="body-create-purchase-order">
        <div className="create-po-content">
          <div className="create-po-instructions">
            <p>Select a unit to create a purchase order for.</p>
          </div>

          <div className="units-list">
            {units.length > 0 && !isSearching ? (
              units.map((unit) => (
                <div
                  key={unit.id}
                  className={`unit-card ${isCreating ? 'disabled' : ''}`}
                  onClick={() => !isCreating && handleCreatePurchaseOrder(unit)}
                >
                  <div className="unit-card-header">
                    <div className="unit-card-header-name">{unit.unit_name || `Unit ${unit.id}`}</div>
                    <div className="unit-card-header-status">
                      <Label
                        title={unit.status || 'N/A'}
                        labelColor={unitStatusLabelColors[unit.status?.toLowerCase()] || 'gray'}
                        minWidth="100px"
                      />
                    </div>
                  </div>
                  <div className="unit-card-details">
                    <div className="unit-detail">
                      <span className="label">Building:</span>
                      <span className="value">{unit.building_id?.building_name || 'N/A'}</span>
                    </div>
                    <div className="unit-detail">
                      <span className="label">Project:</span>
                      <span className="value">
                        {unit.building_id?.project_id?.project_commercial_name ||
                          unit.building_id?.project_id?.project_placeholder_name || 'N/A'}
                      </span>
                    </div>
                    {unit.address && (
                      <div className="unit-detail unit-detail-address">
                        <span className="label">Address:</span>
                        <span className="value">{unit.address}</span>
                      </div>
                    )}
                  </div>
                </div>
                
              ))

            ) : isSearching ? (
              <div className="search-loading">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="no-units">
                <LoadingSpinner />
              </div>
            )}
            {isCreating && (
              <div className="creating-overlay">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatePurchaseOrderPage; 